/**
 * `FeedbackButton` — soft-launch tester feedback flow.
 *
 * Renders as a single button (usable in Settings, TopBar, or anywhere). Opens
 * a compact dark dialog with three fields:
 *   • Name (optional)
 *   • Message (required)
 *   • What's clunky (optional)
 *
 * On submit, POSTs to `/api/feedback` which persists to MongoDB and best-
 * effort emails the operator. Never blocks app use — errors show a toast and
 * the dialog stays open so the tradie can retry.
 */
import { useState } from "react";
import { MessageSquare, Loader2, Send } from "lucide-react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export default function FeedbackButton({
  variant = "row",
  className = "",
  testIdPrefix = "feedback",
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [clunky, setClunky] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setName("");
    setMessage("");
    setClunky("");
  };

  const submit = async () => {
    const msg = message.trim();
    if (!msg) {
      toast.error("A short message is required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/feedback", {
        name: name.trim(),
        message: msg,
        whats_clunky: clunky.trim(),
      });
      toast.success("Thanks — feedback received");
      reset();
      setOpen(false);
    } catch (e) {
      devLog.warn("feedback submit failed", e);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const trigger =
    variant === "row" ? (
      <button
        type="button"
        data-testid={`${testIdPrefix}-open`}
        className={`w-full h-14 px-4 rounded-xl bi-surface hover:brightness-110 active:scale-[0.99] transition flex items-center gap-3 border border-transparent hover:border-[#FF9500]/30 text-left ${className}`}
      >
        <span className="w-9 h-9 rounded-lg bg-[#FF9500]/15 text-[#FF9500] inline-flex items-center justify-center">
          <MessageSquare className="w-4 h-4" />
        </span>
        <span className="flex-1 min-w-0">
          <div className="text-sm font-heading font-bold">Send feedback</div>
          <div className="text-[11px] text-white/50 truncate">
            Anything clunky? Missing? Broken? Tell us in 30 seconds.
          </div>
        </span>
      </button>
    ) : (
      <button
        type="button"
        data-testid={`${testIdPrefix}-open`}
        className={`h-9 px-3 rounded-lg bi-ghost-btn text-xs inline-flex items-center gap-1.5 ${className}`}
      >
        <MessageSquare className="w-3.5 h-3.5" /> Feedback
      </button>
    );

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        data-testid={`${testIdPrefix}-dialog`}
        className="bg-[#1F1F1F] border border-white/10 text-white rounded-2xl max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">Send feedback</DialogTitle>
          <DialogDescription className="text-white/60">
            Short and blunt is best. Every message goes straight to the
            BuildIntel team.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <input
            data-testid={`${testIdPrefix}-name`}
            className="w-full bi-input"
            placeholder="Your name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
          />
          <textarea
            data-testid={`${testIdPrefix}-message`}
            className="w-full bi-input resize-none min-h-[120px]"
            placeholder="What worked well or what fell over?"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            maxLength={4000}
            required
          />
          <textarea
            data-testid={`${testIdPrefix}-clunky`}
            className="w-full bi-input resize-none min-h-[70px]"
            placeholder="What's clunky? (optional)"
            value={clunky}
            onChange={(e) => setClunky(e.target.value)}
            rows={2}
            maxLength={1000}
          />
        </div>
        <DialogFooter className="gap-2">
          <button
            type="button"
            data-testid={`${testIdPrefix}-cancel`}
            onClick={() => setOpen(false)}
            className="h-11 px-4 rounded-xl bi-ghost-btn text-xs font-heading font-bold uppercase tracking-wider"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid={`${testIdPrefix}-submit`}
            onClick={submit}
            disabled={busy || !message.trim()}
            className="h-11 px-4 rounded-xl bi-accent-btn text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
